import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { get, set } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import styled from 'styled-components';

import { assetDisplayTitle } from '../../utility/files';
import { formatUploadToArtifact } from '../../utility/general';
import { updateStoryShowcase } from '../../utility/story';
import Accordion from './Accordion';
import AddTile from './AddTile';
import AssetItem from './AssetItem';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import Dropzone from './Dropzone';

import type { Artifact, FileData } from '../../types.ts/story';
import type { Asset } from './AssetItem';
import type { DropzoneAccept } from './Dropzone';

type Props = {
  property:
    | 'organizationLogos'
    | 'organizationArtifacts'
    | 'organizationArtifactsVideo'
    | 'branding.designElements';
};

const BrandKitMediaSection = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const { property } = props;
  const [isUploading, setIsUploading] = useState(false);
  // Only logo deletion asks for confirmation (PLAT-10284); the other media
  // sections keep their immediate delete behavior.
  const [logoToDelete, setLogoToDelete] = useState<Asset | null>(null);
  const items = get(videoCreator.organization, property);
  const { id, title, message, accept, gridSize } =
    getPropsFromProperty(property);

  const deleteAsset = async (asset: Asset) => {
    const organization = videoCreator.organization!;
    const updated = get(organization, property)?.filter(i => i.id !== asset.id);
    set(organization, property, updated);
    await datoClientStore.albumRepository?.update(organization);
  };

  return (
    <Accordion id={id} title={`${title}${items ? ` (${items.length})` : ''}`}>
      {(property.includes('branding')
        ? videoCreator.organization?.branding
        : items) !== undefined ? (
        <Dropzone
          isEmpty={!items?.length}
          message={message}
          accept={accept as DropzoneAccept}
          isUploading={isUploading}
          onDrop={async files => {
            if (!files.length) return;
            setIsUploading(true);
            const results = await Promise.all(
              files.map(async file => {
                const fileName = file.name.split('.')[0];
                const fileData: FileData & { fileName: string } = {
                  type: 'artifact',
                  file,
                  fileName,
                  alt: '',
                  title: property === 'organizationArtifacts' ? '' : fileName,
                };
                const newUpload =
                  await datoClientStore.assetRepository?.uploadFile(fileData);
                return formatUploadToArtifact(newUpload!, {});
              }),
            );
            const organization = videoCreator.organization;
            set(organization!, property, [
              ...(get(organization, property) || []),
              ...results,
            ]);
            await datoClientStore.albumRepository?.update(organization!);
            if (videoCreator.story) {
              updateStoryShowcase(videoCreator.story, organization!);
            }
            if (property === 'organizationArtifacts') {
              videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
                organization!.id,
                results,
              );
            } else if (property === 'organizationArtifactsVideo') {
              videoCreator.statusUpdateManager.addOrganizationVideoArtifactsToWatch(
                organization!.id,
                results,
              );
            }
            setIsUploading(false);
          }}
          key={items?.map(i => `${i.id}_${i.title}`).join(',')}
        >
          {({ openDialog }) => {
            return (
              <Container gridSize={gridSize}>
                {items?.length ? (
                  <>
                    {items.map(item => (
                      <AssetItem
                        key={item.id}
                        asset={
                          property !== 'organizationArtifactsVideo'
                            ? item
                            : {
                                ...item,
                                title: assetDisplayTitle(item),
                                url: (item as Artifact).video?.thumbnailUrl,
                                downloadUrl: item.url,
                              }
                        }
                        onDelete={async asset => {
                          if (property === 'organizationLogos') {
                            setLogoToDelete(asset);
                            return;
                          }
                          await deleteAsset(asset);
                        }}
                      />
                    ))}
                    <AddTile className="add-tile" onClick={openDialog} />
                  </>
                ) : null}
              </Container>
            );
          }}
        </Dropzone>
      ) : null}

      {logoToDelete && (
        <ConfirmDeleteDialog
          title={`Delete "${assetDisplayTitle(logoToDelete)}"?`}
          onCancel={() => setLogoToDelete(null)}
          onConfirm={() => {
            // Close immediately for one-click responsiveness; the delete
            // updates MobX synchronously and persists in the background.
            const asset = logoToDelete;
            setLogoToDelete(null);
            void deleteAsset(asset);
          }}
        >
          <p>This logo will be removed from your Brand Kit.</p>
        </ConfirmDeleteDialog>
      )}
    </Accordion>
  );
});

const getPropsFromProperty = (property: string) => {
  switch (property) {
    case 'organizationLogos':
      return {
        id: 'logos',
        title: 'Logos',
        message: 'Add your logo(s)',
        accept: 'images',
        gridSize: 140,
      };
    case 'organizationArtifacts':
      return {
        id: 'photos',
        title: 'Photos',
        message: 'Add your photo(s)',
        accept: 'images',
        gridSize: 160,
      };
    case 'organizationArtifactsVideo':
      return {
        id: 'videos',
        title: 'Videos',
        message: 'Add your video(s)',
        accept: 'videos',
        gridSize: 160,
      };
    case 'branding.designElements':
      return {
        id: 'design-elements',
        title: 'Design Elements',
        message: 'Add your design element(s)',
        accept: 'images',
        gridSize: 140,
      };
    default:
      return {
        id: '',
        title: '',
        message: '',
        accept: undefined,
        gridSize: 140,
      };
  }
};

const Container = styled.div<{ gridSize: number }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${({ gridSize }) => gridSize}px, 1fr)
  );
  gap: 24px;

  > .add-tile {
    align-self: center;
  }
`;

export default BrandKitMediaSection;
